import {makeAutoObservable} from 'mobx';

import {
  hasFlag,
  OnBoardingBits,
  setFlag,
  User,
  UserDetailed,
  userAvatarsLength,
} from '@/domain';

import {WebUserDetailed} from './types';

class UserStore {
  userData: WebUserDetailed | null = null;
  private avatarsTokenQuery: string = '';
  private _onboardings: OnBoardingBits = OnBoardingBits.NONE;

  constructor() {
    makeAutoObservable(this);
  }

  get onboardings() {
    return this._onboardings;
  }

  setOnboardings(onboardings: OnBoardingBits) {
    this._onboardings = onboardings;
  }

  get loaded() {
    return Boolean(this.userData);
  }

  setAvatarsTokenQuery(query: string) {
    this.avatarsTokenQuery = query;
  }

  setUserData(userData: UserDetailed) {
    this.userData = userData;
  }

  setTranslateLanguage(lang: string) {
    if (!this.userData) {
      return;
    }

    this.userData.tr_lang = lang;
  }

  isCurrentUser({nickname}: Pick<User, 'nickname'>) {
    return this.userData?.nickname === nickname;
  }

  isPassedOnboarding(name: OnBoardingBits) {
    return hasFlag(this._onboardings, name);
  }

  passedOnboarding(name: OnBoardingBits) {
    this._onboardings = setFlag(this._onboardings, name, true);
  }

  setVerified(value: boolean) {
    if (!this.userData) {
      return;
    }
    this.userData.verified = value;
  }

  setLocationVerified(
    now = Date.now(),
    location?: {
      cityId?: number;
      countryCode?: string;
    }
  ) {
    if (!this.userData) {
      return;
    }
    this.userData.location_verified_at = now;
    if (location?.cityId !== undefined) {
      this.userData.city_id = location.cityId;
    }
    if (location?.countryCode !== undefined) {
      this.userData.country_code = location.countryCode;
    }
  }

  getAvatars() {
    if (!this.userData) {
      return [];
    }

    return this.userData.avatars.map((id, index) => ({
      id,
      url: this.getAvatarAccessUrl(this.userData?.avatar_urls?.[index]),
    }));
  }

  getAvatarAccessUrl(url?: string) {
    if (!url) {
      return '';
    }

    // images from public folder of the project
    if (url.startsWith('/')) {
      return url;
    }

    return `${url}?${this.avatarsTokenQuery}`;
  }

  getUserBestAvatarUrl(
    user: {avatar_urls?: string[]; avatar_url?: string} | undefined | null
  ) {
    if (!user) {
      return '';
    }

    if (user.avatar_url) {
      return this.getAvatarAccessUrl(user.avatar_url);
    }

    return this.getAvatarAccessUrl(user.avatar_urls?.[0]);
  }

  getRestAvatarUrls(user: User) {
    if (!Array.isArray(user.avatar_urls)) {
      return [];
    }

    return user.avatar_urls.slice(1).map(this.getAvatarAccessUrl.bind(this));
  }

  get profileProgress(): number {
    const user = this?.userData;

    if (!user) {
      return 0;
    }

    let progress = 0;
    if (user.avatars) {
      progress = userAvatarsLength(user) * 3.125; //25
    }
    if (user.about && user.about.length > 0) {
      progress += 5; //30
    }
    if (user.school) {
      progress += 5; //35
    }
    if (user.school_info) {
      progress += 5; //40
    }
    if (user.work) {
      progress += 5; //45
    }
    if (user.work_info) {
      progress += 5; //50
    }
    if (user.interests && user.interests.length > 0) {
      progress += 5; //55
    }
    if (user.languages) {
      progress += 5; //60
    }
    if (user.verified) {
      progress += 7.5; //67.5
    }
    if (user.height_sm !== 0) {
      progress += 2.5; //70
    }
    if (user.weight_kg !== 0) {
      progress += 2.5; //72.5
    }
    if (user.sex_orientation !== 0) {
      progress += 2.5; //75
    }
    if (user.star_sign !== 0) {
      progress += 2.5; //77.5
    }
    if (user.exercise !== 0) {
      progress += 2.5; //80
    }
    if (user.education_level !== 0) {
      progress += 2.5; //82.5
    }
    if (user.marital_status !== 0) {
      progress += 2.5; //85
    }
    if (user.kids !== 0) {
      progress += 2.5; //87.5
    }
    if (user.drinking !== 0) {
      progress += 2.5; //90
    }
    if (user.smoking !== 0) {
      progress += 2.5; //92.5
    }
    if (user.pets !== 0) {
      progress += 2.5; //95
    }
    if (user.religion !== 0) {
      progress += 2.5; //97.5
    }
    if (user.values !== 0) {
      progress += 2.5; //100
    }
    if (progress > 100) {
      progress = 100;
    }
    return Math.round(progress);
  }
}

export default new UserStore();
